import {z} from 'zod';
import {LeaderboardVisibility} from '../../rooms/room.editor';
import {runfileIdentificationSchema} from '../../rooms/runfile.identification';
import {protectionSchema} from '../presentationProtection/protectionSchema';

export const ANONYMOUS_PRESENTER_VALUE = 'default';
export const ROOM_ANONYMOUS_PRESENTER_EMAIL_MAX_LENGTH = 100;

const baseRoomSchema = z.object({
    roomName: z.string().min(1, 'roomsPage.error.nameTooShort').max(40, 'roomsPage.error.nameTooLong').trim(),
    presentationId: z.string().min(1, 'roomsPage.error.presentationRequired'),
    presenterId: z.string().nullable().optional(),
    presenterEmail: z
        .string()
        .email('roomsPage.error.invalidEmail')
        .max(ROOM_ANONYMOUS_PRESENTER_EMAIL_MAX_LENGTH, 'roomsPage.error.nameTooLong')
        .optional()
        .or(z.literal('')),
    protection: protectionSchema,
    showLeaderboard: z.boolean().optional(),
    leaderboardVisibility: z.nativeEnum(LeaderboardVisibility).optional(),
    leaderboardSize: z.number().int().min(1).optional(),
    leaderboardShowPersonalRank: z.boolean().optional(),
    startTime: z.string().datetime().optional().or(z.literal('')),
    endTime: z.string().datetime().optional().or(z.literal('')),
});

const isEndAfterStart = (data: {startTime?: string; endTime?: string}): boolean => {
    if (!data.startTime || !data.endTime) return true;
    return new Date(data.endTime).getTime() > new Date(data.startTime).getTime();
};

const END_AFTER_START_ISSUE: {message: string; path: (string | number)[]} = {
    message: 'roomsPage.error.endBeforeStart',
    path: ['endTime'],
};

const isStartNotInPast = (data: {startTime?: string}): boolean => {
    if (!data.startTime) return true;
    return new Date(data.startTime).getTime() >= Date.now();
};

const START_IN_PAST_ISSUE: {message: string; path: (string | number)[]} = {
    message: 'roomsPage.error.startInPast',
    path: ['startTime'],
};

const hasAnonymousPresenterEmail = (data: {presenterId?: string | null; presenterEmail?: string}): boolean =>
    !(data.presenterId === ANONYMOUS_PRESENTER_VALUE && !data.presenterEmail);

const PRESENTER_EMAIL_REQUIRED: {message: string; path: (string | number)[]} = {
    message: 'roomsPage.error.presenterEmailRequired',
    path: ['presenterEmail'],
};

export const createRoomValidator = baseRoomSchema
    .refine(hasAnonymousPresenterEmail, PRESENTER_EMAIL_REQUIRED)
    .refine(isStartNotInPast, START_IN_PAST_ISSUE)
    .refine(isEndAfterStart, END_AFTER_START_ISSUE);

export type CreateRoomFormType = z.infer<typeof createRoomValidator>;

export const editRoomValidator = baseRoomSchema
    .extend({
        _id: z.string().min(1),
        identification: runfileIdentificationSchema.optional(),
    })
    .refine(hasAnonymousPresenterEmail, PRESENTER_EMAIL_REQUIRED)
    .refine(isEndAfterStart, END_AFTER_START_ISSUE);

export type EditRoomFormType = z.infer<typeof editRoomValidator>;
